/**
 * "Usually ~6s" — expected answer times for the ticking timer.
 *
 * Starts from the measured table the server also uses
 * (functions/latency-table.json), then blends in this device's own recent
 * answers (network and time of day matter), stored locally per tier+mode.
 */

import table from '../../functions/latency-table.json';

const KEY = 'apr-latency-samples-v1';
const MAX_SAMPLES = 8;

function readSamples() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

export function recordLatencySample(tier, mode, totalMs) {
  if (!Number.isFinite(totalMs) || totalMs <= 0) return;
  try {
    const all = readSamples();
    const key = `${tier}:${mode}`;
    const list = Array.isArray(all[key]) ? all[key] : [];
    list.push(Math.round(totalMs));
    all[key] = list.slice(-MAX_SAMPLES);
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch (e) {
    // storage blocked — the table alone is fine
  }
}

/**
 * @returns {{p50Ms:number, p90Ms:number, firstTokenP50Ms:number|null, source:string}|null}
 */
export function expectedLatency(tier, mode, serverExpected) {
  const tableEntry = table?.tiers?.[tier]?.[mode] || table?.tiers?.[tier]?.archive || null;
  const base = serverExpected || tableEntry;
  if (!base) return null;
  const local = median((readSamples()[`${tier}:${mode}`] || []).slice(-MAX_SAMPLES));
  if (local && (readSamples()[`${tier}:${mode}`] || []).length >= 3) {
    return {
      p50Ms: Math.round(local * 0.6 + base.p50Ms * 0.4),
      p90Ms: Math.round(Math.max(base.p90Ms, local * 1.4)),
      firstTokenP50Ms: base.firstTokenP50Ms || null,
      source: 'blend',
    };
  }
  return { p50Ms: base.p50Ms, p90Ms: base.p90Ms, firstTokenP50Ms: base.firstTokenP50Ms || null, source: 'table' };
}

/** 6.4s → "6s", 72s → "1m 12s" */
export function formatDuration(ms, { decimals = 0 } = {}) {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const seconds = ms / 1000;
  if (seconds < 60) return decimals ? `${seconds.toFixed(decimals)}s` : `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

export const LATENCY_TABLE = table;
